import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaFileInvoice } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../../Modal";
import JobMonitorReportComponent from "../../transaction/JobMonitor/JobMonitorReportComponent";
import "../../../css/EvInvManager.css";
import { FORMS, MODULES } from "../../../const";
import useUserCompany from "../../../hooks/useUserCompany";
import BulkEvInvComponent from "./Bulk/BulkEvInvComponent";
import SingleEvInvComponent from "./Single/SingleEvInvComponent";
import {
  CurrencyRenderer,
  ResizableScrollingTextRenderer,
} from "../../renderer/StyleRenderer";
import SingleTaxBreakdown from "./Single/SingleTaxBreakdown";
import BulkTaxBreakdown from "./Bulk/BulkTaxBreakdown";
import { invEvActions } from "../../../store/finance/inv-ev/inv-ev-slice";
import InfoList from "../../InfoList";
import { formatFromJsonDate } from "../../../dateConfig";
import { User } from "react-feather";

const gridHeaderConst = {
  CHARGE: "CHARGE-HEADER",
};

const EvInvComponent = forwardRef(({ auditInfo }, ref) => {
  const isBulkMode = useSelector((state) => state.invEv.bulkMode);
  const isEditMode = useSelector((state) => state.invEv.editMode);
  const savedUpdateResponse = useSelector(
    (state) => state.invEv.fetchedResponse,
  );
  const categoryId = useSelector((state) => state.invEv.categoryId);

  const dispatch = useDispatch();

  const user = useSelector((state) => state.user);

  const userCompany = useUserCompany();
  const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;

  const [drawerTab, setDrawerTab] = useState(1);
  const reportModalRef = useRef();
  const taxBreakdownModalRef = useRef();

  const [modalKey, setModalKey] = useState(1);

  useEffect(() => {
    if (isEditMode && savedUpdateResponse) {
      const evInvJobIds = [];
      for (let evInvDetail of savedUpdateResponse.jobEvInvoiceDetails) {
        if (evInvJobIds.length === 0) {
          dispatch(invEvActions.setSelectedJob(evInvDetail.chhId));
        }
        if (
          !evInvJobIds.some(
            (item) =>
              item.jobId === evInvDetail.eidJob &&
              item.chgId === evInvDetail.chhId,
          )
        ) {
          evInvJobIds.push({
            jobId: evInvDetail.eidJob,
            chgId: evInvDetail.chhId,
          });
        }
      }
      dispatch(invEvActions.setJobIds(evInvJobIds));
    }
  }, [dispatch, isEditMode, savedUpdateResponse]);

  const formatter = useMemo(
    () =>
      userCurrencyCode
        ? Intl.NumberFormat("en-US", {
            style: "currency",
            currency: userCurrencyCode,
          })
        : null,
    [userCurrencyCode],
  );

  const isChargeHeaderRow = (params) => {
    return params.data.section === gridHeaderConst.CHARGE;
  };

  // Col-span rule

  let chargeCellClassRules = useMemo(
    () => ({
      "header-cell": 'data.section === "CHARGE-HEADER"',
    }),
    [],
  );

  const commonNoConsolidationView = useMemo(
    () => [
      {
        headerName: "Charges",
        field: "chgCharges",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellRenderer: ResizableScrollingTextRenderer,
        cellRendererParams: (params) => ({
          threshold: 15,
        }),
      },
      {
        headerName: "Description",
        field: "chdDescription",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellRenderer: ResizableScrollingTextRenderer,
      },
      {
        headerName: "Job",
        field: "jobCode",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
      },
      {
        headerName: "Total",
        field: "chdTotal",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        cellRenderer: CurrencyRenderer,
        cellRendererParams: (params) => ({
          formatter: formatter,
        }),
      },
    ],
    [chargeCellClassRules, formatter],
  );

  const commonConsolidated = useMemo(
    () => [
      {
        headerName: "Charges",
        field: "chgCharges",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        colSpan: (params) => (isChargeHeaderRow(params) ? 3 : 1),
        cellRendererSelector: (param) => {
          if (isChargeHeaderRow(param)) {
            return null;
          }
          return {
            component: ResizableScrollingTextRenderer,
            params: {
              threshold: 15,
            },
          };
        },
      },
      {
        headerName: "Description",
        field: "chdDescription",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellRenderer: ResizableScrollingTextRenderer,
      },
      {
        headerName: "Job",
        field: "jobCode",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
      },
      {
        headerName: "Total",
        field: "chdTotal",
        authenticated: true,
        module: MODULES.Finance,
        primaryForm: FORMS.EV_INV_MANAGER.DETAILS.form,
        cellClassRules: chargeCellClassRules,
        cellRenderer: CurrencyRenderer,
        cellRendererParams: (params) => ({
          formatter: formatter,
        }),
      },
    ],
    [chargeCellClassRules, formatter],
  );

  const getGridData = useCallback(async (savedRes, chhId = null) => {
    const gridDataWithChargeType = [];
    let id = 2;
    for (const chargeDetails of savedRes.jobEvInvoiceDetails) {
      const eidChargeTypeValue = chargeDetails.eidChargeValue;
      const chargeStatusName = chargeDetails.eidStatusValue;
      if (chhId !== null) {
        if (chargeDetails.chhId === chhId) {
          gridDataWithChargeType.push({
            id: id++,
            chgCharges: eidChargeTypeValue,
            job: chargeDetails.eidJob,
            eidCharge: chargeDetails.eidCharge,
            chdDescription: chargeDetails.eidDescription,
            chdCurrency: chargeDetails.eidCurrency,
            chdTotal: chargeDetails.eidTotal,
            chdTaxAmount: chargeDetails.eidTaxAmount,
            chdTotalWithTax:
              chargeDetails.eidTotal + chargeDetails.eidTaxAmount,
            chdStatus: chargeDetails.eidStatus,
            eidPickChargeId: chargeDetails.eidPickChargeId,
            chdId: chargeDetails.eidId,
            chdExcId: chargeDetails.eidExcId,
            chdExcRate: chargeDetails.eidExcRate,
            chhId: chargeDetails.chhId,
            jobEvInvoicesTaxDetailList:
              chargeDetails.jobEvInvoicesTaxDetailList,
            chargeStatusName,
          });
        }
      } else {
        gridDataWithChargeType.push({
          chgCharges: eidChargeTypeValue,
          eidCharge: chargeDetails.eidCharge,
          chdDescription: chargeDetails.eidDescription,
          chdCurrency: chargeDetails.eidCurrency,
          chdTotal: chargeDetails.eidTotal,
          chdTaxAmount: chargeDetails.eidTaxAmount,
          chdTotalWithTax: chargeDetails.eidTotal + chargeDetails.eidTaxAmount,
          chdStatus: chargeDetails.eidStatus,
          eidPickChargeId: chargeDetails.eidPickChargeId,
          chdId: chargeDetails.eidId,
          chdExcId: chargeDetails.eidExcId,
          chdExcRate: chargeDetails.eidExcRate,
          chdCurCode: chargeDetails.eidCurCode,
          chhId: chargeDetails.chhId,
          jobEvInvoicesTaxDetailList: chargeDetails.jobEvInvoicesTaxDetailList,
          chargeStatusName,
        });
      }
    }
    return gridDataWithChargeType;
  }, []);

  const openTaxBreakdown = async (data) => {
    setModalKey((prevState) => prevState + 1);
    taxBreakdownModalRef.current?.showModal();
  };

  const openReportModal = () => {
    setModalKey((prevState) => prevState + 1);
    reportModalRef.current?.showModal();
  };

  return (
    <>
      {!isEditMode && (
        <Modal
          ref={taxBreakdownModalRef}
          closeButton={true}
          minHeight="700px"
          onCloseCleanUp={() => {
            dispatch(invEvActions.setIsSubmitLoading(false));
          }}
          clickOutSide={false}
        >
          {isBulkMode ? (
            <BulkTaxBreakdown
              key={modalKey}
              formatter={formatter}
              commonConsolidated={commonConsolidated}
              commonNoConsolidationView={commonNoConsolidationView}
            />
          ) : (
            <SingleTaxBreakdown
              key={modalKey}
              gridConfig={commonNoConsolidationView}
              formatter={formatter}
            />
          )}
        </Modal>
      )}
      <Modal ref={reportModalRef} closeButton={true} minHeight="500px">
        <JobMonitorReportComponent key={modalKey} evinv={true} />
      </Modal>
      <div className="drawer-component">
        <div className="ui top attached tabular menu">
          <div
            className={`item ${drawerTab === 1 ? "active" : ""}`}
            onClick={() => {
              setDrawerTab(1);
            }}
          >
            <span>
              <FaFileInvoice size={15} style={{ marginRight: "5px" }} />
            </span>
            <label>{categoryId === 1 ? "INV" : "EV"} Generator</label>
          </div>
        </div>
        <div
          className="ui bottom attached active tab segment"
          style={{ display: drawerTab === 1 ? "block" : "none" }}
        >
          {isBulkMode ? (
            <BulkEvInvComponent
              openTaxBreakdown={openTaxBreakdown}
              openReportModal={openReportModal}
              getGridData={getGridData}
              formatter={formatter}
              username={user.username}
              commonNoConsolidationView={commonNoConsolidationView}
              commonConsolidated={commonConsolidated}
            />
          ) : (
            <SingleEvInvComponent
              openTaxBreakdown={openTaxBreakdown}
              openReportModal={openReportModal}
              getGridData={getGridData}
              commonNoConsolidationView={commonNoConsolidationView}
              username={user.username}
              formatter={formatter}
            />
          )}
        </div>
      </div>
      {isEditMode && (
        <InfoList
          title="User Information"
          content={[
            {
              title: "Created By",
              value: auditInfo?.createdBy ? auditInfo?.createdBy : "N/A",
            },
            {
              title: "Created Date",
              value: auditInfo?.createdDate
                ? formatFromJsonDate(auditInfo?.createdDate)
                : "N/A",
            },
            {
              title: "Last Modified By",
              value: auditInfo?.lastModifiedBy
                ? auditInfo?.lastModifiedBy
                : "N/A",
            },
            {
              title: "Last Modified Date",
              value: auditInfo?.lastModifiedDate
                ? formatFromJsonDate(auditInfo?.lastModifiedDate)
                : "N/A",
            },
          ]}
          Icon={User}
        />
      )}
    </>
  );
});

EvInvComponent.displayName = "evInvComponent";

export default EvInvComponent;
